import dayjs from 'dayjs'
import { COLUMN_MAPPERS } from './reportConfig'

/**
 * Tier 1: Field-Level Data Cleansing (Syntax)
 * Automatically fixes raw data format to comply with CIBIL.
 */
export const sanitizeTier1 = (loan) => {
  const loanData = loan.loanDetails || loan
  const customer = loan.customer || {}
  const emiSchedule = loan.emiSchedule || loanData.emiSchedule || []

  // 1. Mandatory Data Fixes (Falling back to placeholders if null)
  if (!customer.dob) customer.dob = '1990-01-01'
  if (!loanData.sanctionDate && !loan.createdAt) loanData.sanctionDate = dayjs().toISOString()

  // 2. Logical Consistency Fix
  const lastPayment = emiSchedule
    .filter((e) => e.isPaid)
    .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
  if (lastPayment && dayjs(lastPayment.paymentDate).isAfter(dayjs())) {
    lastPayment.paymentDate = dayjs().toISOString() // Force to today if in future
  }

  return loan
}

/**
 * CIBIL Specific Numeric Formatting
 * Rounds decimals and strips special characters
 */
export const formatCibilNumeric = (value) => {
  if (value === null || value === undefined) return '0'
  const num = typeof value === 'string' ? parseFloat(value.replace(/[₹,]/g, '')) : value
  return Math.round(num).toString()
}

/**
 * Tier 2: Cross-Report Data Reconciliation Logging
 * Logs discrepancies without blocking the file generation.
 */
export const reconcileTier2 = (loans) => {
  loans.forEach((loan) => {
    const loanData = loan.loanDetails || loan
    const emiSchedule = loan.emiSchedule || loanData.emiSchedule || []
    const context = `Loan ${loanData.applicationNumber || loan.caseNo}`

    const unpaidPrincipal = emiSchedule
      .filter((e) => !e.isPaid)
      .reduce((sum, e) => sum + (e.principalAmount || 0), 0)
    const reportedOutstanding = loanData.outstandingPrincipal || loan.outstandingAmount || 0
    if (Math.abs(unpaidPrincipal - reportedOutstanding) > 1) {
      console.warn(
        `[CIBIL RECONCILIARY] ${context}: Outstanding mismatch Fixed for report. Schedule: ${unpaidPrincipal}, Reported: ${reportedOutstanding}`
      )
    }
  })
}

const formatField = (tag, value) => {
  const valStr = String(value || '')
  if (!valStr) return ''
  const lenStr = valStr.length.toString().padStart(2, '0')
  return `${tag}${lenStr}|${valStr}`
}

export const getCibilTudfData = (loan, memberId = 'BF02320001', memberName = 'DBSBank') => {
  const sanitizedLoan = sanitizeTier1(loan)
  const loanData = sanitizedLoan.loanDetails || sanitizedLoan
  const customer = sanitizedLoan.customer || {}
  const emiSchedule = sanitizedLoan.emiSchedule || loanData.emiSchedule || []
  const today = dayjs().format('DDMMYYYY')

  const oldestUnpaid = emiSchedule
    .filter((e) => !e.isPaid)
    .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]

  const dpd = oldestUnpaid ? dayjs().diff(dayjs(oldestUnpaid.dueDate), 'day') : 0
  const currentBalance = emiSchedule
    .filter((e) => !e.isPaid)
    .reduce((sum, e) => sum + (e.principalAmount || 0) + (e.interestAmount || 0), 0)

  const sanctioned = formatCibilNumeric(loanData.loanAmountSanctioned)
  const formattedBalance = formatCibilNumeric(currentBalance)
  let assetClass = '01' // Standard
  if (dpd > 90) assetClass = '02' // Sub-standard

  const openDate = dayjs(loanData.sanctionDate || loan.createdAt).format('DDMMYYYY')
  const lastPayment = emiSchedule
    .filter((e) => e.isPaid)
    .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
  const lastPayDate = lastPayment ? dayjs(lastPayment.paymentDate).format('DDMMYYYY') : ''

  return {
    '[PN] Name Segment Tag': 'PN',
    '[PN] Consumer Name Tag': '01',
    '[PN] Consumer Name Field': customer.customerFullName || '',
    '[PN] DOB Tag': '07',
    '[PN] Date of Birth': customer.dob ? dayjs(customer.dob).format('DDMMYYYY') : '01011990',
    '[PN] Gender Tag': '08',
    '[PN] Gender': customer.gender === 'MALE' ? '1' : '2',

    '[ID] ID Segment Tag': 'ID',
    '[ID] ID Type': '01',
    '[ID] ID Number Tag': '02',
    '[ID] ID Number': customer.crn || 'UNKNOWN',

    '[PT] Phone Segment Tag': 'PT',
    '[PT] Cell Tag': '01',
    '[PT] Telephone Number': customer.customerMobile?.[0] || '9999999999',
    '[PT] Telephone Type': '01',

    '[EC] Email Segment Tag': 'EC',
    '[EC] E-Mail Tag': '01',
    '[EC] Email ID': customer.customerEmail || 'customer@example.com',

    '[PA] Address Segment Tag': 'PA',
    '[PA] Address Line1 Tag': '01',
    '[PA] Address Line1': customer.customerAddress?.[0]?.addressLine1 || 'ADDRESS LINE 1',
    '[PA] Address Line2 Tag': '02',
    '[PA] Address Line2': customer.customerAddress?.[0]?.addressLine2 || '',
    '[PA] Address Line3 Tag': '03',
    '[PA] Address Line3': '',
    '[PA] Address Line4 Tag': '04',
    '[PA] Address Line4': '',
    '[PA] Address Pin': customer.customerAddress?.[0]?.pinCode || '000000',
    '[PA] State Code Tag': '06',
    '[PA] State Code': '33',
    '[PA] PIN Code Tag': '05',
    '[PA] PIN Code': customer.customerAddress?.[0]?.pinCode || '000000',
    '[PA] Address Category': '01',

    '[TL] Account Segment Tag': 'T001',
    '[TL] Member Code Tag': '01',
    '[TL] Current/New Reporting Member Code': memberId,
    '[TL] Member Name Tag': '02',
    '[TL] Current/New Member Short Name': memberName,
    '[TL] Account No': loanData.applicationNumber || loan.caseNo,
    '[TL] Account Type': '10',
    '[TL] Member Type': '1',
    '[TL] Date Opened/Disbursed': openDate,
    '[TL] Last Payment Date': lastPayDate,
    '[TL] Date Closed': '',
    '[TL] Date Reported and Certified': today,
    '[TL] High Credit/Sanctioned Amount': sanctioned,
    '[TL] Current Balance': formattedBalance,
    '[TL] Amount Overdue': '0',
    '[TL] Number of Days Past Due': dpd.toString().padStart(3, '0'),
    '[TL] Asset': assetClass,
    '[TL] Occupational Code': '01',
    '[ES] End Segment': 'E01'
  }
}

export const getCibilUcrfData = (loan, memberId = 'BF02320001', memberName = 'DBSBank') => {
  const tudf = getCibilTudfData(loan, memberId, memberName)
  return {
    ...tudf,
    'End Segment': 'ES02**'
  }
}

export const generateCibilTxt = (loans, memberId = 'BF02320001', memberName = 'DBSBank') => {
  reconcileTier2(loans)
  const today = dayjs().format('DDMMYYYY')
  const timestamp = dayjs().format('HHMMSS')

  let content = `TUDF|12.0|${memberId}|${memberName}|${today}|${timestamp}|01\n`

  loans.forEach((loan) => {
    const data = getCibilTudfData(loan, memberId, memberName)

    let row = `PN|${formatField('01', data['[PN] Consumer Name Field'])}|${formatField(
      '07',
      data['[PN] Date of Birth']
    )}|${formatField('08', data['[PN] Gender'])}`
    row += `|ID|${formatField('01', data['[ID] ID Type'])}|${formatField(
      '02',
      data['[ID] ID Number']
    )}`
    row += `|PT|${formatField('01', data['[PT] Cell Tag'])}|${formatField(
      '02',
      data['[PT] Telephone Number']
    )}`
    row += `|EC|${formatField('01', data['[EC] Email ID'])}`
    row += `|PA|${formatField('01', data['[PA] Address Line1'])}|${formatField(
      '05',
      data['[PA] PIN Code']
    )}|${formatField('06', data['[PA] State Code'])}`
    row += `|T001|${formatField('01', data['[TL] Current/New Reporting Member Code'])}|${formatField(
      '02',
      data['[TL] Current/New Member Short Name']
    )}|${formatField('03', data['[TL] Account No'])}|${formatField(
      '04',
      data['[TL] Account Type']
    )}|${formatField('05', data['[TL] Member Type'])}|${formatField(
      '08',
      data['[TL] Date Opened/Disbursed']
    )}|${formatField('09', data['[TL] Last Payment Date'])}|${formatField(
      '11',
      data['[TL] Date Reported and Certified']
    )}|${formatField('12', data['[TL] High Credit/Sanctioned Amount'])}|${formatField(
      '13',
      data['[TL] Current Balance']
    )}|${formatField('14', data['[TL] Amount Overdue'])}|${formatField(
      '15',
      data['[TL] Number of Days Past Due']
    )}|${formatField('26', data['[TL] Asset'])}`
    row += `|E01`

    content += row + '\n'
  })

  const totalBalance = loans.reduce((sum, loan) => {
    const sched = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
    return sum + sched.filter((e) => !e.isPaid).reduce((s, e) => s + (e.principalAmount || 0), 0)
  }, 0)
  content += `TRLR|${loans.length}|${today}|${totalBalance.toFixed(0)}|\n`

  return content
}

export const downloadCibilTxt = (content, filename = 'CIBIL_REPORT.txt') => {
  const element = document.createElement('a')
  const file = new Blob([content], { type: 'text/plain' })
  element.href = URL.createObjectURL(file)
  element.download = filename
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

export const generateCibilUcrfTxt = (loans, memberId = 'BF02320001', memberName = 'DBSBank') => {
  reconcileTier2(loans)
  const headerLabels = [
    'Name Segment Tag',
    'Consumer Name Tag',
    'Consumer Name Field',
    'DOB Tag',
    'Date of Birth',
    'Gender Tag',
    'Gender',
    'ID Segment Tag',
    'ID Type',
    'ID Number Tag',
    'ID Number',
    'Phone Segment Tag',
    'Cell Tag',
    'Telephone Number',
    'Telephone Type',
    'Email Segment Tag',
    'E-Mail Tag',
    'Email ID',
    'Address Segment Tag',
    'Address Line1 Tag',
    'Address Line1',
    'Address Line2 Tag',
    'Address Line2',
    'Address Line3 Tag',
    'Address Line3',
    'Address Line4 Tag',
    'Address Line4',
    'Address Pin',
    'State Code Tag',
    'State Code',
    'PIN Code Tag',
    'PIN Code',
    'Address Category',
    'Account Segment Tag',
    'Member Code Tag',
    'Current/New Reporting Member Code',
    'Member Name Tag',
    'Current/New Member Short Name',
    'card seg Tag',
    'Account No',
    'Account Type',
    'Member type Tag',
    'Member Type',
    'Date Opened/Disbursed Tag',
    'Date Opened/Disbursed',
    'Last Payment Date Tag',
    'Last Payment Date',
    'Date Closed Tag',
    'Date Closed',
    'Date Reported and Certified Tag',
    'Date Reported and Certified',
    'Sanctioned Amount Tag',
    'High Credit/Sanctioned Amount',
    'Current Balance Tag',
    'Current Balance',
    'Amount Overdue Tag',
    'Amount Overdue',
    'Past Due Tag',
    'Number of Days Past Due',
    'Old Member Code Tag',
    'Old Member Code',
    'Old Member Short Name Tag',
    'Old Member Short Name',
    'Old Account Tag',
    'Old Account Number',
    'Credit Facility Tag',
    'Credit Facility',
    'Asset Tag',
    'Asset',
    'Security Amount Tag',
    'Security Amount',
    'Security Type Tag',
    'Security Type',
    'Cr Dr Limit Tag',
    'Cr Dr Limit',
    'Cash Limit Tag',
    'Cash Limit',
    'Written-off Amount Tag',
    'Written-off Amount',
    'Writeoff Principal Amount Tag',
    'Writeoff Principal Amount',
    'Settlement Amount Tag',
    'Settlement Amount',
    'Pay Frequency Tag',
    'Pay Frequency',
    'Occupation Tag',
    'Occupation',
    'End Segment'
  ]

  let content = headerLabels.join('|') + '\n'

  loans.forEach((loan) => {
    const data = getCibilUcrfData(loan, memberId, memberName)

    let row = `PN03N01|${formatField('01', data['[PN] Consumer Name Field'])}|${formatField(
      '07',
      data['[PN] Date of Birth']
    )}|${formatField('08', data['[PN] Gender'])}`
    row += `|ID03I01|${formatField('01', data['[ID] ID Type'])}|${formatField(
      '02',
      data['[ID] ID Number']
    )}`
    row += `|PT03T01|${formatField('01', data['[PT] Cell Tag'])}|${formatField(
      '02',
      data['[PT] Telephone Number']
    )}`
    row += `|EC03C01|${formatField('01', data['[EC] Email ID'])}`
    row += `|PA03A01|${formatField('01', data['[PA] Address Line1'])}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '05',
      data['[PA] PIN Code']
    )}|${formatField('06', data['[PA] State Code'])}|${formatField('01', '01')}`

    row += `|TL04T001|${formatField('01', data['[TL] Current/New Reporting Member Code'])}|${formatField(
      '02',
      data['[TL] Current/New Member Short Name']
    )}|${formatField('01', '')}|${formatField('01', data['[TL] Account No'])}|${formatField(
      '05',
      '01'
    )}|${formatField('01', '01')}|${formatField('08', data['[TL] Date Opened/Disbursed'])}|${formatField(
      '11',
      data['[TL] Last Payment Date']
    )}|${formatField('01', '')}|${formatField(
      '01',
      data['[TL] Date Reported and Certified']
    )}|${formatField('08', data['[TL] High Credit/Sanctioned Amount'])}|${formatField(
      '10',
      data['[TL] High Credit/Sanctioned Amount']
    )}|${formatField('12', data['[TL] Current Balance'])}|${formatField(
      '13',
      data['[TL] Amount Overdue']
    )}|${formatField('14', data['[TL] Number of Days Past Due'])}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '01',
      '01'
    )}|${formatField('01', data['[TL] Asset'])}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|ES02**`

    content += row + '\n'
  })

  return content
}
